import { useEffect, useState } from "react";
import { harvestsApi } from "../../services/harvestsApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { Field } from "../../components/common/Field";
import { WithdrawalNotice } from "../../components/hives/WithdrawalNotice";
import { hivesApi } from "../../services/hivesApi";

const TYPES = ["Honey", "Wax", "Propolis", "Pollen"];
const UNITS = ["Lbs", "Kg", "Gal", "L"];

// Deliberately NOT an option list, unlike the rest of the #196 sweep.
//
// app.Harvests.grade is CHAR(1) with CHECK (grade IN ('A','B','C')), and
// HarvestCreateRequest pins it to ^[ABC]$. Behind a SelectOrAdd, "+ Add
// new..." would save the user's own grade to their option list, offer it
// back, and then 400 when they tried to use it. A closed enumeration gets a
// plain <select>; see the header of api/functions/options.py.
const GRADES = [
  { value: "A", label: "A — table honey, clear and clean" },
  { value: "B", label: "B — good, minor cloudiness or debris" },
  { value: "C", label: "C — baking or feed grade" },
];

export function HarvestTab({ hive }) {
  /* Best-effort, like every other reader of this list: a failure says
     nothing rather than blocking a harvest somebody needs to record. */
  const [withdrawal, setWithdrawal] = useState([]);
  const { notify } = useToast();
  const [harvests, setHarvests] = useState(null);
  const [form, setForm] = useState({ harvest_type: "Honey", quantity: "", unit: "Lbs", grade: "", date: new Date().toISOString().slice(0, 10) });

  async function load() {
    const r = await harvestsApi.list();
    setHarvests(r.harvests.filter((h) => h.hive_id === hive.hive_id));
  }

  useEffect(() => {
    hivesApi.withdrawal()
      .then((r) => setWithdrawal(r.withdrawal || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [hive.hive_id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    if (!form.quantity) return notify("Enter a quantity", "warning");
    try {
      await harvestsApi.create({
        hive_id: hive.hive_id,
        harvest_type: form.harvest_type,
        quantity: Number(form.quantity),
        unit: form.unit,
        // Omitted rather than sent empty: the column is nullable and "" fails
        // the ^[ABC]$ pattern, so a blank picker would 400 the whole harvest.
        grade: form.grade || undefined,
        date: form.date,
      });
      notify("Harvest logged", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not log harvest", "danger");
    }
  }

  return (
    <div>
      <Card title="Harvest History (Honey, Wax, Propolis, Pollen)" className="section-spacing">
        <DataTable
          rows={harvests}
          loading={!harvests}
          rowKey="harvest_id"
          emptyTitle="No harvests logged for this hive"
          emptyDescription="What this hive has produced, harvest by harvest."
          columns={[
            { key: "date", header: "Date", render: (r) => new Date(r.date).toLocaleDateString() },
            { key: "harvest_type", header: "Type" },
            { key: "quantity", header: "Quantity", render: (r) => `${r.quantity} ${r.unit}` },
            { key: "grade", header: "Grade", render: (r) => r.grade || "—" },
            { key: "batch_number", header: "Batch" },
          ]}
        />
      </Card>

      <Card title="Log Harvest">
        {/* THE SIXTH DOOR (#404). Honey leaves a hive by five routes I
            enumerated and this one, which I missed: the harvest form on
            the hive's OWN page. Reported from the web app -- "the warning
            appears for pulling frames but the log harvest warning doesn't
            appear" -- and it is the one-path bug this codebase keeps
            finding, in the middle of the feature written to avoid it.

            Mobile's equivalent tab had it from the start, which is what
            made the gap invisible: the behaviour was right on the device
            being tested and absent on the desk. */}
        <WithdrawalNotice withdrawal={withdrawal} hiveId={hive?.hive_id} actionType="Harvest" />

        {/* Real labels throughout (#205). "Quantity" was a placeholder, which
            disappears the moment you type -- and the three other controls had
            nothing naming them at all. */}
        <div style={{ display: "grid", gap: "var(--space-3)", maxWidth: 360 }}>
          <Field label="What you took">
            {(id, aria) => (
              <select id={id} {...aria} value={form.harvest_type} onChange={(e) => setForm((f) => ({ ...f, harvest_type: e.target.value }))} className="touch-target">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </Field>
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Field label="How much">
                {(id, aria) => <input id={id} {...aria} type="number" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} className="touch-target" />}
              </Field>
            </div>
            <Field label="Unit">
              {(id, aria) => (
                <select id={id} {...aria} value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} className="touch-target">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              )}
            </Field>
          </div>
          {/* Grade only for honey. The column allows it on any harvest type,
              but grading wax or pollen A/B/C means nothing, and the Harvests
              page has been rendering an empty Grade column since there was
              never a way to fill it (#196). */}
          {form.harvest_type === "Honey" && (
            <Field label="Grade (optional)" hint="Leave blank if you don't grade.">
              {(id, aria) => (
                <select id={id} {...aria} value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))} className="touch-target">
                  <option value="">Not graded</option>
                  {GRADES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              )}
            </Field>
          )}
          <Field label="Date">
            {(id, aria) => <input id={id} {...aria} type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="touch-target" />}
          </Field>
          <Button onClick={submit}>Log Harvest</Button>
        </div>
      </Card>
    </div>
  );
}
